import { useEffect, useMemo, useState } from 'react';
import { useDashboardData } from './hooks/useDashboardData';
import type { NewsMode, WeekKpiTotals } from './types';
import { DEFAULT_LOCATION, NEWS_MODE_LABELS } from './constants';
import ChartCard from './components/common/ChartCard';
import LoadingState from './components/common/LoadingState';
import ErrorState from './components/common/ErrorState';
import LocationBarChart from './components/charts/LocationBarChart';
import TitlesEngagementChart from './components/charts/TitlesEngagementChart';
import TopicTensionChart from './components/charts/TopicTensionChart';
import VipHypeChart from './components/charts/VipHypeChart';
import TopicsMap from './components/charts/TopicsMap';
import ToneChart from './components/charts/ToneChart';
import CompassChart from './components/charts/CompassChart';
import WordCloudChart from './components/charts/WordCloudChart';
import { fetchWeeks, fetchWeekKpis } from './api/bqQueries';
import { formatNumber } from './utils/dataTransforms';

function App() {
  const [week, setWeek] = useState<string | null>(null);
  const [weeks, setWeeks] = useState<string[]>([]);
  const [newsMode, setNewsMode] = useState<NewsMode>('political');
  const [selectedLocation, setSelectedLocation] = useState(DEFAULT_LOCATION);
  const [kpis, setKpis] = useState<WeekKpiTotals | null>(null);
  const [appError, setAppError] = useState<string | null>(null);
  const { data, loading, error, initialized } = useDashboardData(week ?? '', selectedLocation);

  const formatWeekRange = (startDateStr: string) => {
    const start = new Date(startDateStr);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const locationOptions = useMemo(() => data?.locations ?? [], [data]);

  const defaultLocation = locationOptions.includes(DEFAULT_LOCATION)
    ? DEFAULT_LOCATION
    : locationOptions[0];

  useEffect(() => {
    if (locationOptions.length > 0 && !locationOptions.includes(selectedLocation)) {
      setSelectedLocation(defaultLocation);
    }
  }, [locationOptions, defaultLocation, selectedLocation]);

  useEffect(() => {
    fetchWeeks()
      .then((list) => {
        if (list.length) {
          setWeeks(list);
          setWeek(list[0]);
        } else {
          setAppError('No data available for any week.');
        }
      })
      .catch((err) => {
        setAppError(err instanceof Error ? err.message : 'Failed to fetch weeks');
      });
  }, []);

  useEffect(() => {
    if (!week) return;
    let cancelled = false;
    fetchWeekKpis(week)
      .then((values) => {
        if (!cancelled) setKpis(values);
      })
      .catch(() => {
        if (!cancelled) setKpis(null);
      });
    return () => {
      cancelled = true;
    };
  }, [week]);

  if (!week || (!initialized && (loading || !data))) {
    return <LoadingState message="Warming up the visualization engines…" />;
  }

  if ((error || appError) && !data) {
    return <ErrorState message={error || appError || 'Unknown error'} />;
  }

  if (!data) {
    return null;
  }

  return (
    <div className="app-shell">
      <header className="hero">
        <div className="hero-text">
          <p className="eyebrow">Live Network Intelligence</p>
          <h1>Polarization pulse across Greece</h1>
          <p className="lead">
            Explore how users across ideological segments move, read, and engage with news stories.
            Switch between political-only and general news to uncover shifts in engagement and
            hotspots of attention.
          </p>
        </div>
        <div className="hero-badge">
          <p>powered by</p>
          <img src="/adsquirrel-02.png" alt="AdSquirrel" />
        </div>
        <div className="mode-pill">
          <span>Week</span>
          <select 
            value={week ?? ''} 
            onChange={(e) => setWeek(e.target.value)}
            style={{ 
              background: 'transparent', 
              color: 'white', 
              border: 'none', 
              fontSize: '16px', 
              fontWeight: '600',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {weeks.map((w) => (
              <option key={w} value={w} style={{ background: '#101529' }}>
                {formatWeekRange(w)}
              </option>
            ))}
          </select>
        </div>
        <div className="mode-pill">
          <span>News focus</span>
          <div className="mode-options">
            {(['political', 'general'] as NewsMode[]).map((mode) => (
              <button
                key={mode}
                className={mode === newsMode ? 'active' : ''}
                onClick={() => setNewsMode(mode)}
              >
                {NEWS_MODE_LABELS[mode]}
              </button>
            ))}
          </div>
        </div>
      </header>

      {kpis && (
        <section className="kpi-row">
          <div className="kpi-card">
            <p className="kpi-label">Politics-minded users</p>
            <p className="kpi-value">{formatNumber(kpis.politicsUsers)}</p>
            <span className="kpi-subtext">weekly unique readers</span>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Left count</p>
            <p className="kpi-value">{formatNumber(kpis.leftUsers)}</p>
            <span className="kpi-subtext">people engaging</span>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Neutral count</p>
            <p className="kpi-value">{formatNumber(kpis.neutralUsers)}</p>
            <span className="kpi-subtext">people engaging</span>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Right count</p>
            <p className="kpi-value">{formatNumber(kpis.rightUsers)}</p>
            <span className="kpi-subtext">people engaging</span>
          </div>
        </section>
      )}

      <section className="grid single">
        <ChartCard
          title="VIP Hype"
          subtitle="Engagement around political leaders across different ideological segments"
          footnote="Click on a leader's bar to drill down into the top topics their readers are engaging with."
          loading={loading}
        >
          <VipHypeChart 
            hypeData={data.vipHype[newsMode]} 
            topicsData={data.vipTopics[newsMode]} 
          />
        </ChartCard>
      </section>

      <section className="grid single">
        <ChartCard
          title="Buzzword radar"
          subtitle="Word cloud of top-engagement topics for the selected location"
          footnote="Sized by engagement score; switch news scope to compare."
          loading={loading}
        >
          <WordCloudChart
            data={data.wordCloud}
            newsMode={newsMode}
            onNewsModeChange={setNewsMode}
            locationOptions={locationOptions}
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
          />
        </ChartCard>
      </section>

      <section className="grid two-column">
        <ChartCard
          title="Engagement leaders"
          subtitle="Top stories by engagement score"
          footnote="Search a city or region to review the narrative that resonates the most."
          loading={loading}
        >
          {data && (
            <TitlesEngagementChart
              newsMode={newsMode}
              onNewsModeChange={setNewsMode}
              locationOptions={locationOptions}
              defaultLocation={selectedLocation}
              week={week}
              onLocationSync={setSelectedLocation}
              onLocationChange={setSelectedLocation}
              selectedLocation={selectedLocation}
            />
          )}
        </ChartCard>

        <ChartCard
          title="Topic tension: Left vs Right"
          subtitle="See topics that dominate only one side of the spectrum"
          footnote="Topics ordered by the largest left vs right share gaps (top 8 each)."
          loading={loading}
        >
          {data && (
            <TopicTensionChart
              data={data.topicTension}
              newsMode={newsMode}
              onNewsModeChange={setNewsMode}
            />
          )}
        </ChartCard>
      </section>

      <section className="grid two-column">
        <ChartCard
          title="Tone of voice across segments"
          subtitle="Fact-first or opinion-heavy? Track each audience’s mix."
          footnote="Stacked shares of factual vs opinionated reading; hover to see objectivity scores."
          loading={loading}
        >
          <ToneChart
            data={data?.objectivity ?? { political: [], general: [] }}
            locationOptions={locationOptions}
            defaultLocation={defaultLocation}
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
          />
        </ChartCard>

        {data && (
          <ChartCard
            title="Political compass"
            subtitle="Where audiences sit on polarity vs objectivity"
            footnote="Scatter density of users by ideological lean (X) and objectivity (Y)."
            loading={loading}
          >
            <CompassChart
              data={data.compass}
              locationOptions={locationOptions}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
            />
          </ChartCard>
        )}
      </section>

      <section className="grid single">
        <ChartCard
          title="Topic map of Greece"
          subtitle="Click on a location to reveal dominant narratives"
          footnote="Topics are derived from the most read stories per location. Select a location to unlock the precise titles driving buzz."
          loading={loading}
        >
          {data && (
            <TopicsMap
              topics={data.topics}
              coordinates={data.coordinates}
              newsMode={newsMode}
              onNewsModeChange={setNewsMode}
              defaultLocation={defaultLocation}
              week={week}
            />
          )}
        </ChartCard>
      </section>
    </div>
  );
}

export default App;
